/**
 * Small helpers for the FlipCost / Paystack billing flow.
 *  - detectBillingCurrency: picks ZAR for SA visitors, USD for everyone else.
 *  - formatPrice: just a thin wrapper so the Pricing page can swap labels.
 */

/**
 * Pick a billing currency based on the visitor's locale.
 *
 *  - URL override `?currency=usd|zar`         (always wins — for testing / support)
 *  - localStorage cached choice                (so override persists across nav)
 *  - SA visitors (timezone Africa/Johannesburg or locale region ZA) → ZAR
 *  - Everyone else                                                   → USD
 *
 * Wrapped in try/catch so old browsers (no Intl.Locale) fall back to USD
 * — better to default to USD for the global audience than to incorrectly
 * charge a non-SA visitor in ZAR.
 */
export function detectBillingCurrency() {
  try {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search || "");
      const qp = (params.get("currency") || "").toUpperCase();
      if (qp === "USD" || qp === "ZAR") {
        try { window.localStorage.setItem("fc_currency", qp); } catch { /* ignore */ }
        return qp;
      }
      try {
        const cached = (window.localStorage.getItem("fc_currency") || "").toUpperCase();
        if (cached === "USD" || cached === "ZAR") return cached;
      } catch { /* ignore quota / privacy mode */ }
    }
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.includes("Africa/Johannesburg")) return "ZAR";
    // Some browsers report just "en" or "en-US" — Intl.Locale().maximize()
    // resolves these to a full BCP-47 tag we can read a region from.
    try {
      const region = new Intl.Locale(navigator.language || "en-US")
        .maximize().region;
      if (region === "ZA") return "ZAR";
    } catch { /* Intl.Locale unavailable — keep going to default */ }
    return "USD";
  } catch {
    return "USD";
  }
}

// Pricing table mirrored from backend PLAN_PRICES (in major units, not cents).
export const PLAN_PRICING = {
  USD: {
    single: { label: "$19", amount: 19, cadence: "one-off" },
    pro: { label: "$15", amount: 15, cadence: "/ month" },
    unlimited: { label: "$35", amount: 35, cadence: "/ month" },
    single_spacecost: { label: "$12", amount: 12, cadence: "one-off" },
    room_pack: { label: "$5", amount: 5, cadence: "one-off · +3 rooms" },
    symbol: "$",
  },
  ZAR: {
    single: { label: "R280", amount: 280, cadence: "one-off" },
    pro: { label: "R280", amount: 280, cadence: "/ month" },
    unlimited: { label: "R640", amount: 640, cadence: "/ month" },
    single_spacecost: { label: "R180", amount: 180, cadence: "one-off" },
    room_pack: { label: "R80", amount: 80, cadence: "one-off · +3 rooms" },
    symbol: "R",
  },
};

export const PLAN_LIMIT_LABEL = {
  free: "Demo only — buy an unlock to save a project",
  single: "1 unlocked project",
  pro: "Up to 5 active projects",
  unlimited: "Unlimited projects",
  single_spacecost: "1 SpaceCost project · 3 rooms",
  room_pack: "+3 extra rooms",
};
